import Link from 'next/link';
import styled, { css } from 'styled-components';
import { Image } from 'shared/components/Image';
import isFunction from 'lodash/isFunction';

import { IRelatedProductData } from 'productTemplate/types/FactsWindowData';
import { normalizeValue } from 'shared/utils/normalizeValue';
import { toggleVisible } from 'styles/theme/animation';
import { useWindowSize } from 'shared/hooks/useWindowSize';
import { variableFontRegular } from 'styles/theme/text';

interface ISearchResultImageGrid {
  align: 'left' | 'center' | 'right';
  disableLazyLoad?: boolean;
  isCrawler?: boolean;
  onClick?: () => void;
  imageSize?: string;
  isClickable?: boolean;
  product: { data?: Record<string, any>; value?: string } | IRelatedProductData;
  missingAssetPath?: string;
  showPtNames?: boolean;
}

export const SearchResultImageGrid = ({
  align,
  disableLazyLoad = true,
  isCrawler = false,
  imageSize = '150px',
  isClickable = true,
  onClick,
  product,
  missingAssetPath,
  showPtNames = false,
}: ISearchResultImageGrid) => {
  const { isMobile } = useWindowSize();
  let slug = '';
  let imgUrl;
  let productName;
  let category;

  if ('title' in product) {
    slug = product.slug;
    // in order to show the placeholder image when there is no image url, we need to set it to undefined, it cannot be an empty string
    imgUrl = product.pictureUrl || undefined;
    productName = product.title;
    category = product.category;
  } else if ('data' in product) {
    const { data, value } = product;
    slug = data?.slug;
    imgUrl = data?.image_url || data?.pictureUrl;
    productName = value;
    category = data?.category;
  }

  const handleOnClick = () => {
    if (isFunction(onClick)) {
      onClick();
    }
  };

  return (
    <SearchResultImageGrid.Wrapper data-qa="search_result_image_grid_item">
      {isClickable ? (
        <Link
          href={`/${category === 'shoes' ? 'sneakers' : 'apparel'}/${slug}`}
          passHref
          prefetch={false}
        >
          <SearchResultImageGrid.Link onClick={handleOnClick}>
            {!isCrawler && (
              <Image
                alt={normalizeValue(productName)}
                data-qa="search_result_product_image"
                disableLazyLoad={disableLazyLoad}
                sizes={imageSize}
                src={imgUrl}
                missingAssetPath={missingAssetPath}
              />
            )}
            {((showPtNames && isMobile) || isCrawler) && (
              <SearchResultImageGrid.FixedName data-qa="search_result_product_name">
                {productName}
              </SearchResultImageGrid.FixedName>
            )}
          </SearchResultImageGrid.Link>
        </Link>
      ) : (
        <SearchResultImageGrid.Link>
          {!isCrawler && (
            <Image
              alt={normalizeValue(productName)}
              data-qa="search_result_product_image"
              disableLazyLoad={disableLazyLoad}
              sizes={imageSize}
              src={imgUrl}
              missingAssetPath={missingAssetPath}
            />
          )}
          {((showPtNames && isMobile) || isCrawler) && (
            <SearchResultImageGrid.FixedName data-qa="search_result_product_name">
              {productName}
            </SearchResultImageGrid.FixedName>
          )}
        </SearchResultImageGrid.Link>
      )}
      {(!showPtNames || !isMobile) && (
        <SearchResultImageGrid.Name align={align} data-qa="search_result_product_name">
          {productName}
        </SearchResultImageGrid.Name>
      )}
    </SearchResultImageGrid.Wrapper>
  );
};

SearchResultImageGrid.Wrapper = styled.div`
  position: relative;
  height: 100%;
`;

SearchResultImageGrid.Link = styled.a`
  display: block;
`;

SearchResultImageGrid.Name = styled.div<{
  align?: ISearchResultImageGrid['align'];
}>`
  position: absolute;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
  ${variableFontRegular({ size: '9px', height: '10px' })};
  visibility: hidden;
  opacity: 0;

  @media (hover: hover) and (pointer: fine) {
    a:hover ~ & {
      ${toggleVisible};
    }
  }

  ${({ align }) => {
    if (align === 'center') {
      return css`
        left: 50%;
        transform: translateX(-50%);
      `;
    }

    if (align === 'right') {
      return css`
        right: 0;
      `;
    }

    return css`
      left: 0;
    `;
  }};
`;

SearchResultImageGrid.FixedName = styled.div`
  visibility: hidden;
  ${variableFontRegular({ size: '9px', height: '10px' })};

  @media (max-width: ${({ theme }) => theme.breakpoints.small}px) {
    opacity: 1;
    visibility: visible;
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    display: -webkit-box !important;
  }
`;
